<template>
  <base-modal
    show
    size="md"
    :has-close-btn="!globalModals.length"
    :modal-id="MODAL_ID"
    :heading="heading"
    :cancel-button="formStep === PURCHASE_STEPS.CART"
    @close:modal="closeModal"
  >
    <template v-if="formStep === PURCHASE_STEPS.CART" #default>
      <ugc-purchase-mod
        :mod="mod"
        :step="formStep"
        :game-name="game.name"
        :token-name="tokenName"
      />

      <ugc-purchase-wallet
        show-button
        :game="game"
        :mod="mod"
        :token-name="tokenName"
        :balance="balance"
        :deficit="deficit"
        :price="mod.price"
        :loading-balance="loadingBalance"
      />

      <p
        v-if="isMonetizationWebSelfRefundsDisabled(game.monetization_options)"
        class="tw-mt-4"
      >
        <i18n-t keypath="marketplace.ugc_not_refundable">
          <template #ugc_plural>
            {{ ugcName(game, true, false).value }}
          </template>
          <template #terms>
            <a
              class="mod-link"
              href="https://mod.io/terms"
              @click.prevent="openTermsOfUseModal()"
              >{{ $t('terms') }}
            </a>
          </template>
          <template #refund>
            <a
              class="mod-link"
              href="https://mod.io/refund"
              @click.prevent="openRefundPolicyModal()"
              >{{ $t('refund') }}</a
            >
          </template>
          <template #ugc>
            {{ ugcName(game, true, true).value }}
          </template>
        </i18n-t>
      </p>
    </template>
    <template v-else-if="formStep === PURCHASE_STEPS.CHECKOUT" #default>
      <ugc-purchase-mod
        :mod="mod"
        :step="formStep"
        :game-name="game.name"
        :token-name="tokenName"
      />

      <base-divider class="tw-mt-6 tw-mb-4" />

      <ugc-purchase-wallet
        :step="formStep"
        :token-name="tokenName"
        :price="mod.price"
        :balance="balance"
        :deficit="deficit"
      />
    </template>
    <template v-else-if="formStep === PURCHASE_STEPS.RECEIPT" #default>
      <ugc-purchase-mod
        :mod="mod"
        :step="formStep"
        :game-name="game.name"
        :token-name="tokenName"
        :show-header-and-price="false"
      />

      <base-divider class="tw-mt-6 tw-mb-4" />

      <p class="tw-mb-6">
        <i18n-t keypath="marketplace.ugc_purchase_success">
          <template #ugc>
            {{ ugcName(game, true, true).value }}
          </template>
          <template #library>
            <base-link :to="{ name: LIBRARY_ROUTE }">{{
              $t('library')
            }}</base-link>
          </template>
        </i18n-t>
      </p>

      <p
        class="tw-text-xs tw-font-medium tw-uppercase tw-tracking-widest tw-mb-3"
      >
        {{ $t('summary') }}
      </p>
      <table class="tw-mb-6">
        <tbody>
          <tr>
            <td class="tw-opacity-70 tw-pr-8">{{ $t('date') }}</td>
            <td>{{ dataCheckout.purchase_date }}</td>
          </tr>
          <tr>
            <td class="tw-opacity-70 tw-pr-8">
              {{ $t('marketplace.transaction_id') }}
            </td>
            <td>{{ dataCheckout.transaction_id }}</td>
          </tr>
          <tr>
            <td class="tw-opacity-70 tw-pr-8">
              {{ $t('marketplace.payment_method') }}
            </td>
            <td>
              {{
                $t('marketplace.game_virtual_currency', {
                  virtual_currency: dataCheckout.gross_amount.toLocaleString(),
                })
              }}
            </td>
          </tr>
          <tr>
            <td class="tw-opacity-70 tw-pr-8">
              {{ $t('marketplace.total_paid') }}
            </td>
            <td>
              {{ dataCheckout.gross_amount.toLocaleString() }}
              {{ tokenName }}
            </td>
          </tr>
        </tbody>
      </table>

      <base-divider class="tw-mt-6 tw-mb-4" />

      <ugc-purchase-wallet
        :step="formStep"
        :token-name="tokenName"
        :balance="balance"
        :deficit="deficit"
        :loading-balance="loadingBalance"
      />
    </template>

    <template v-if="formStep === PURCHASE_STEPS.CHECKOUT" #footer-top>
      <base-checkbox
        test-id="checkout-terms"
        class="tw-mb-3"
        large
        :error-gap="false"
        :background="false"
        :errors="errors.monTermsAgreed"
        :model-value="monTermsAgreed"
        @input="(input) => updateAgreed(input)"
      >
        <template #label>
          <span class="tw-text-theme">
            <i18n-t keypath="marketplace.agreement">
              <template #terms>
                <a
                  class="mod-link"
                  href="https://mod.io/terms"
                  @click.prevent="openTermsOfUseModal()"
                  >{{ $t('terms') }}
                </a>
              </template>
              <template #refund>
                <a
                  class="mod-link"
                  href="https://mod.io/refund"
                  @click.prevent="openRefundPolicyModal()"
                  >{{ $t('refund') }}</a
                >
              </template>
            </i18n-t>
          </span>
        </template>
      </base-checkbox>
    </template>

    <template #footer>
      <base-button
        test-id="cart-btn"
        :primary="formStep !== PURCHASE_STEPS.RECEIPT"
        :secondary="formStep === PURCHASE_STEPS.RECEIPT"
        class="tw-w-full sm:tw-w-auto tw-min-w-[10.5rem]"
        :disabled="
          (formStep === PURCHASE_STEPS.CART && !canAfford) || isLoading
        "
        :status="formStep !== PURCHASE_STEPS.RECEIPT ? status : null"
        @click="button.onClick"
      >
        {{ button.text }}
      </base-button>
    </template>
  </base-modal>

  <teleport to="body">
    <cancel-transaction-modal
      v-if="isCancelModalOpen"
      @confirm="closeModal(true)"
      @close:modal="hideCancelModal()"
    />
  </teleport>
</template>

<script>
import UgcPurchaseWallet from '@components/Monetization/Components/UgcPurchaseWallet.vue'
import CancelTransactionModal from '@shared/components/Modals/CancelTransactionModal.vue'
import UgcPurchaseMod from '@components/Monetization/Components/UgcPurchaseMod.vue'
import { subscriptionStore, monetizationStore, purchasesStore } from '@stores'
import { isMonetizationWebSelfRefundsDisabled } from '@helpers/bitHelper.js'
import { setGlobalModal, globalModals } from '@composables/useModal.js'
import { MODAL_AGREEMENT } from '@config/globalModals.js'
import { humanDateTimeFormat } from '@helpers/utils.js'
import { VIRTUAL_TOKEN } from '@config/monetization.js'
import { onMounted, computed, toRefs, ref } from 'vue'
import { monTermsConfig } from '@config/validation.js'
import { LIBRARY_ROUTE } from '@config/routeNames.js'
import { useI18n } from 'vue-i18n'
import {
  useResourceValue,
  useMonetization,
  useValidate,
  useStatus,
  useModal,
  useAsync,
  addToast,
} from '@composables'
import {
  getOrCreateBuyerGameWalletRequest,
  subscribeToModRequest,
  checkoutModRequest,
} from '@services'
import {
  AGREEMENT_TYPE_REFUND,
  AGREEMENT_TYPE_TOU,
  PURCHASE_STEPS,
} from '@config/options.js'
import {
  RESOURCE_ALREADY_SUBSCRIBED,
  MOD_ALREADY_OWNED,
} from '@shared/js/config/errors.js'

const MODAL_ID = 'purchase-modal'

export default {
  components: {
    CancelTransactionModal,
    UgcPurchaseWallet,
    UgcPurchaseMod,
  },
  props: {
    game: {
      type: Object,
      required: true,
    },
    mod: {
      type: Object,
      required: true,
    },
  },
  emits: ['close:purchasemodal', 'purchase:complete'],
  setup(props, { emit }) {
    const { game, mod } = toRefs(props)

    const formStep = ref(PURCHASE_STEPS.CART)
    const monTermsAgreed = ref(false)
    const initialBalance = ref()
    const balance = ref(0)
    const deficit = ref(0)

    const { setGameWallet } = useMonetization()
    const { ugcName } = useResourceValue()
    const { t } = useI18n()
    const { validate, errors, hasErrors } = useValidate(
      monTermsConfig.validation
    )
    const {
      isModalOpen: isCancelModalOpen,
      showModal: showCancelModal,
      hideModal: hideCancelModal,
    } = useModal()

    const { addSubscription, removeSubscription } = subscriptionStore()
    const { transactionKey, setTransactionKey } = monetizationStore()
    const { addPurchase } = purchasesStore()

    const {
      loading: loadingBalance,
      error: errorBalance,
      data: dataBalance,
      run: runBalance,
    } = useAsync(
      getOrCreateBuyerGameWalletRequest,
      t('marketplace.error_wallet')
    )

    // Checkout process
    const {
      errorRef: errorRefCheckout,
      loading: loadingCheckout,
      error: errorCheckout,
      data: dataCheckout,
      run: runCheckout,
    } = useAsync((data) =>
      checkoutModRequest(game.value.id, mod.value.id, data)
    )

    const {
      errorRef: subscribeErrorRef,
      error: subscribeError,
      run: subscribeToMod,
    } = useAsync(
      () =>
        subscribeToModRequest({
          gameId: game.value.id,
          modId: mod.value.id,
        }),
      t('subscribe_fail')
    )

    const isLoading = computed(
      () => loadingBalance.value || loadingCheckout.value
    )

    const tokenName = computed(() => game.value?.token_name)

    const modTransactionKey = computed(
      () => transactionKey.value[`${game.value.id}-mod-${mod.value.id}`]
    )

    const { updateStatus, statusType, status } = useStatus({
      loading: isLoading,
      error: errorCheckout,
    })

    const heading = computed(() => {
      switch (formStep.value) {
        case PURCHASE_STEPS.CART:
          return t('marketplace.order_summary')

        case PURCHASE_STEPS.CHECKOUT:
          return t('marketplace.checkout')

        case PURCHASE_STEPS.RECEIPT:
          return t('marketplace.ugc_purchase_confirmed', {
            ugc: ugcName(game, true).value,
          })
      }

      return ''
    })

    const button = computed(() => {
      switch (formStep.value) {
        case PURCHASE_STEPS.CART:
          return {
            text: t('marketplace.proceed_to_checkout'),
            onClick: checkout,
          }

        case PURCHASE_STEPS.CHECKOUT:
          return {
            text: t('marketplace.confirm_purchase'),
            onClick: purchase,
          }

        case PURCHASE_STEPS.RECEIPT:
          return {
            text: t('marketplace.continue_browsing'),
            onClick: closeModal,
          }
      }

      return {}
    })

    const canAfford = computed(
      () => balance.value - deficit.value >= mod.value.price
    )

    onMounted(() => {
      getWalletBalance()
      if (!modTransactionKey.value) initTransactionKey()
    })

    function initTransactionKey() {
      setTransactionKey(
        game.value.id,
        `mod-${mod.value.id}`,
        Date.now().toString(36)
      )
    }

    function closeModal(confirm = false) {
      if (confirm || formStep.value !== PURCHASE_STEPS.CHECKOUT) {
        emit('close:purchasemodal')
        setTransactionKey(game.value.id, `mod-${mod.value.id}`, null)
        return
      }

      showCancelModal()
    }

    function updateAgreed(input) {
      monTermsAgreed.value = input

      validate('monTermsAgreed', monTermsAgreed.value)
    }

    function openRefundPolicyModal() {
      setGlobalModal({
        modal: MODAL_AGREEMENT,
        data: { agreementType: AGREEMENT_TYPE_REFUND, noOpacity: true },
      })
    }

    function openTermsOfUseModal() {
      setGlobalModal({
        modal: MODAL_AGREEMENT,
        data: { agreementType: AGREEMENT_TYPE_TOU, noOpacity: true },
      })
    }

    function updateStep(step) {
      formStep.value = step
    }

    async function getWalletBalance() {
      await runBalance()

      if (!errorBalance.value) {
        balance.value = dataBalance.value.balance || 0
        deficit.value = dataBalance.value.deficit || 0

        if (!initialBalance.value) {
          initialBalance.value = dataBalance.value.balance || 0
        }

        setGameWallet(game.value.id, dataBalance.value)
      } else {
        closeModal(true)
      }
    }

    function checkout() {
      if (canAfford.value) updateStep(PURCHASE_STEPS.CHECKOUT)
    }

    async function purchase() {
      validate('monTermsAgreed', monTermsAgreed.value)

      if (hasErrors()) {
        updateStatus(statusType.ERROR)
        return
      }

      const data = {
        payment_method_id: dataBalance.value.payment_method_id,
        type: VIRTUAL_TOKEN,
        display_amount: mod.value.price,
        refund_accepted: true,
        terms_accepted: true,
        idempotent_key: modTransactionKey.value,
      }

      await runCheckout(data)

      if (!errorCheckout.value) {
        emit('purchase:complete')
        dataCheckout.value.purchase_date = humanDateTimeFormat(
          dataCheckout.value.purchase_date
        )
        balance.value = dataCheckout.value.balance || 0
        deficit.value = dataCheckout.value.deficit || 0
        addPurchase(mod.value.id, mod.value)
        subscribe()
        updateStep(PURCHASE_STEPS.RECEIPT)
        updateStatus(statusType.SUCCESS)
        setTransactionKey(game.value.id, `mod-${mod.value.id}`, null)
      } else if (errorRefCheckout.value === MOD_ALREADY_OWNED) {
        addToast({
          title: t('marketplace.error_already_purchased', {
            ugc: ugcName(game, true).value,
          }),
          isSuccess: true,
        })
        addPurchase(mod.value.id, mod.value)
        updateStatus(statusType.SUCCESS)
        subscribe()
        closeModal(true)
      } else {
        initTransactionKey()
        updateStep(PURCHASE_STEPS.CART)
        addToast({
          title: t('marketplace.error_complete'),
          text: errorCheckout.value,
          isError: true,
          errorRef: errorRefCheckout.value,
        })
      }
    }

    async function subscribe() {
      addSubscription(mod.value.id, mod.value)

      await subscribeToMod()

      if (
        subscribeError.value &&
        subscribeErrorRef.value !== RESOURCE_ALREADY_SUBSCRIBED
      ) {
        removeSubscription(mod.value.id)
      }
    }

    return {
      isMonetizationWebSelfRefundsDisabled,
      openRefundPolicyModal,
      openTermsOfUseModal,
      isCancelModalOpen,
      hideCancelModal,
      PURCHASE_STEPS,
      monTermsAgreed,
      loadingBalance,
      LIBRARY_ROUTE,
      globalModals,
      dataCheckout,
      updateAgreed,
      closeModal,
      isLoading,
      canAfford,
      tokenName,
      MODAL_ID,
      formStep,
      heading,
      balance,
      deficit,
      ugcName,
      button,
      errors,
      status,
    }
  },
}
</script>
